import React from 'react';
import TextInput from '../../../../../../common/components/TextInput';
import { ErrorTooltip } from '../../../../../../common/components/ErrorTooltip/ErrorTooltip';
import Switch from '../../../../../../common/components/Switch/Switch';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import {
  CampaignBudgetType,
  SpecialAdCategory,
} from '@mm/state/interfaces/adAccounts';
import Hint from '../../../../../../common/components/Hint/Hint';
import Checkbox from '../../../../../../common/components/Checkbox';
import { TooltipContainer } from '../../../../../../common/components/TooltipContainer/TooltipContainer';
import { numericValue } from '../../../utils/utils';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import { QuestionCircleIcon } from '@mm/icons';
import { InfoCircleFilled } from '@ant-design/icons';
import classNames from 'classnames';

type Props = {
  adConfig: AdConfigModel | null;
  campaignName: string;
  campaignBudgetType: CampaignBudgetType | null;
  campaignBudget: number | string | null;
  specialAdCategory: SpecialAdCategory;
  onChangeCampaignName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCampaignBudget: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurCampaignName: () => void;
  onBlurCampaignBudget: () => void;
  onBudgetOptimizationToggle: () => void;
  onToggleSpecialCategory: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCampaignBudgetType: (id: CampaignBudgetType) => void;
  onChangeSpecialCategory: (id: SpecialAdCategory) => void;
  budgetOptimizationOn: boolean;
  currency: string;
  campaignNameValidations?: string[];
  campaignBudgetValidations?: string[];
  usedInSlideout?: boolean;
};

const campaignBudgets = [
  { id: CampaignBudgetType.DAILY, name: 'Daily' },
  { id: CampaignBudgetType.LIFETIME, name: 'Lifetime' },
];

const specialCategories = [
  { id: SpecialAdCategory.EMPLOYMENT, name: 'Employment' },
  { id: SpecialAdCategory.HOUSING, name: 'Housing' },
  { id: SpecialAdCategory.CREDIT, name: 'Credit' },
];

export const AdCampaignStep = (props: Props) => {
  const {
    adConfig,
    campaignName,
    campaignBudgetType,
    campaignBudget,
    specialAdCategory,
    onChangeCampaignName,
    onChangeCampaignBudget,
    onBlurCampaignName,
    onBlurCampaignBudget,
    campaignNameValidations,
    campaignBudgetValidations,
    budgetOptimizationOn,
    onBudgetOptimizationToggle,
    onChangeCampaignBudgetType,
    onToggleSpecialCategory,
    onChangeSpecialCategory,
    currency,
    usedInSlideout,
  } = props;

  const draft = adConfig?.draft;
  const inProgress = adConfig?.publishingProcess;
  const disabled = !adConfig || !draft || inProgress;
  const specialAdCategoryChecked = Boolean(adConfig?.specialAdCategory);

  return (
    <>
      <div className="ad-detail__inputs-container">
        <div
          className={classNames('ad-detail__input-container', {
            'ad-detail__input-container-slideout': usedInSlideout,
          })}
        >
          <div className="ad-detail__field-title">Campaign Name</div>
          <div className="ad-detail__text-input-container">
            <TextInput
              className="ad-detail__input"
              placeholder="Enter campaign name"
              value={campaignName}
              disabled={inProgress}
              onChange={onChangeCampaignName}
              onBlur={onBlurCampaignName}
            />
            <ErrorTooltip
              classPrefix="ad-detail"
              validation={campaignNameValidations}
              hoverMode
            />
          </div>
        </div>
      </div>
      <div className="ad-config-detail__optimization-container">
        <Switch
          checked={budgetOptimizationOn}
          disabled={disabled}
          onChange={onBudgetOptimizationToggle}
          label=""
        />
        <a
          href="https://www.facebook.com/business/help/153514848493595"
          target="_blank"
          className="ad-config-detail__optimization-link"
        >
          Campaign Budget Optimization
        </a>
      </div>
      {budgetOptimizationOn && (
        <div className="ad-detail__inputs-container">
          <div
            className={classNames('ad-config-detail__budget-config', {
              'ad-config-detail__budget-config-slideout': usedInSlideout,
            })}
          >
            <div className="ad-config-detail__budget-selectors">
              <div className="ad-config-detail__budget-selector">
                <div className="ad-detail__field-title">Campaign Budget</div>
                <div className="ad-config-detail__budget-dropdown ad-detail__selector">
                  <SelectorWithArrow
                    selectedItem={campaignBudgets.find(
                      item => item.id === campaignBudgetType,
                    )}
                    disabled={disabled}
                    onChange={onChangeCampaignBudgetType}
                    items={campaignBudgets}
                    arrowColor="#262626"
                    arrowSize={14}
                  />
                </div>
              </div>
              <div className="ad-config-detail__budget-selector">
                <div className="ad-detail__text-input-container">
                  <TextInput
                    className="ad-detail__input"
                    placeholder="Enter budget"
                    value={numericValue(campaignBudget)}
                    disabled={disabled}
                    type="number"
                    onChange={onChangeCampaignBudget}
                    onBlur={onBlurCampaignBudget}
                  />
                  <ErrorTooltip
                    classPrefix="ad-detail"
                    validation={campaignBudgetValidations}
                    hoverMode
                  />
                </div>
              </div>
            </div>
            <div className="ad-detail__subtext">
              <InfoCircleFilled className="ad-detail__info-circle" />
              Budget will be set to the currency used in your ad account (
              {currency})
            </div>
          </div>
        </div>
      )}
      <div className="ad-config-detail__special-category-checkbox-container">
        <Checkbox
          className="ad-config-detail__special-category-checkbox"
          labelClassName="ad-config-detail__special-category-checkbox-label"
          isChecked={specialAdCategoryChecked}
          disabled={disabled}
          onToggle={onToggleSpecialCategory}
          label={
            <div className="ad-config-detail__special-category-label">
              I'm creating a campaign for ads in Special Ad Category
            </div>
          }
        />
        <QuestionCircleIcon
          color="var(--color-gray-9)"
          className="ad-detail__subtext-icon"
          onClick={() =>
            window.open(
              'https://www.facebook.com/business/help/298000447747885',
              '_blank',
            )
          }
        />
      </div>
      {specialAdCategoryChecked && (
        <div className="ad-detail__inputs-container">
          <div
            className={classNames('ad-detail__input-container', {
              'ad-detail__input-container-slideout': usedInSlideout,
            })}
          >
            <div className="ad-config-detail__ad-category-dropdown ad-detail__selector">
              <SelectorWithArrow
                selectedItem={specialCategories.find(
                  item => item.id === specialAdCategory,
                )}
                disabled={disabled}
                onChange={onChangeSpecialCategory}
                items={specialCategories}
                arrowColor="#262626"
                arrowSize={14}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
